import { CommonModule } from "@angular/common";
import { Component, Inject, OnDestroy, OnInit, DOCUMENT } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { SavedCard } from "@models/credit-card";
import { InstallmentOption, PaymentStatusUpdate } from "@models/payment";
import { QrCodeModule } from "ng-qrcode";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { firstValueFrom, Subscription } from "rxjs";
import { environment } from "../../../environments/environment";
import { Order } from "../../../models/order";
import { UserInfo } from "../../../models/user";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { ModalSignupComponent } from "../../../shared/components/modal-signup/modal-signup.component";
import { UtilsService } from "../../../utils/utils.service";
import { CepResponse } from "../../interfaces/cep";
import { CartService } from "../../services/cart.service";
import { LocalStorageService } from "../../services/local-storage.service";
import { PaymentNotificationService } from "../../services/payment-notification.service";
import { PaymentService } from "../../services/payment.service";
import { SnackBarService } from "../../services/snack-bar.service";
import { WebSocketService } from "../../services/websocket.service";
import { PaymentProcessingOverlayComponent } from "../../shared/components/payment-processing-overlay/payment-processing-overlay.component";
import { TimeFormatPipe } from "../../shared/pipes/time-format.pipe";

@Component({
  selector: "app-order-checkout",
  imports: [
    ReactiveFormsModule,
    CommonModule,
    QrCodeModule,
    ButtonComponent,
    ProgressSpinnerModule,
    FormsModule,
    PaymentProcessingOverlayComponent,
    TimeFormatPipe,
  ],
  templateUrl: "./order-checkout.component.html",
  styleUrl: "./order-checkout.component.scss",
})
export class OrderCheckoutComponent implements OnInit, OnDestroy {
  checkoutForm!: FormGroup;
  cardForm!: FormGroup;

  // PIX
  pixQrCodeUrl: string = "";
  pixQrCodeImageUrl: string = "";
  pixCopyPasteCode: string = "";
  pixExpiresAt: string = "";
  pixAmount: number = 0;
  statusPaymentPix: string = "Pendente pagamento";
  checkPaymentInterval: any;

  // General
  loading: boolean = false;
  showAddressFields: boolean = false;
  currentUser: UserInfo | null = null;
  isBrazilianCustomer: boolean = true;
  currentOrder: Order | null = null;

  // Credit Card
  showCreditCardForm: boolean = false;
  savedCards: SavedCard[] = [];
  selectedCardId: string | null = null;
  useNewCard: boolean = false;
  cardProcessing: boolean = false;
  cardStatusMsg: string = "";
  cardStatusOk: boolean = false;
  installmentOptions: InstallmentOption[] = [];
  showAllInstallments = false;

  // WebSocket subscriptions
  private paymentStatusSubscription?: Subscription;

  // Payment Processing Overlay
  showPaymentOverlay = false;
  currentPaymentStep = "";
  paymentOverlayError = "";

  constructor(
    private fb: FormBuilder,
    private readonly snackbarService: SnackBarService,
    private cartService: CartService,
    private readonly router: Router,
    private route: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document,
    private utilsService: UtilsService,
    private localStorageService: LocalStorageService,
    private readonly dialog: MatDialog,
    private paymentService: PaymentService,
    private paymentNotificationService: PaymentNotificationService,
    private webSocketService: WebSocketService,
  ) {}

  ngOnInit(): void {
    const rawUser = this.localStorageService.getItem("current_user");
    this.currentUser = this.safeParse<UserInfo>(rawUser);
    const countryCode = (
      this.currentUser?.address?.country ?? "BR"
    ).toUpperCase();
    this.isBrazilianCustomer = countryCode === "BR";

    const phoneNumber = this.currentUser?.phone_number ?? "";
    let numeroComDdd = "";
    if (phoneNumber) {
      try {
        numeroComDdd = this.separarDDIeNumero(phoneNumber).numeroComDdd;
      } catch {
        numeroComDdd = phoneNumber;
      }
    }

    this.checkoutForm = this.fb.group({
      phone: [numeroComDdd, Validators.required],
      street: ["", Validators.required],
      number: ["", Validators.required],
      neighborhood: ["", Validators.required],
      city: ["", Validators.required],
      state: ["", Validators.required],
      zip: ["", Validators.required],
      paymentMethod: [this.isBrazilianCustomer ? "pix" : "credit_card"],
    });

    // Formulário de cartão de crédito para PagarMe
    this.cardForm = this.fb.group({
      holder_name: ["", Validators.required],
      card_number: ["", [Validators.required, Validators.minLength(13)]],
      expiry_month: [
        "",
        [Validators.required, Validators.min(1), Validators.max(12)],
      ],
      expiry_year: [
        "",
        [Validators.required, Validators.min(new Date().getFullYear())],
      ],
      cvv: [
        "",
        [Validators.required, Validators.minLength(3), Validators.maxLength(4)],
      ],
      installments: [1], // Campo comentado no template - sem validação obrigatória
    });

    this.prefillCardForm();

    this.checkoutForm.get("paymentMethod")?.valueChanges.subscribe((method) => {
      void this.handlePaymentMethodChange(method);
    });

    void this.handlePaymentMethodChange(this.checkoutForm.value.paymentMethod);
    this.initializeWebSocket();

    // Load order data
    this.loadOrderData();

    // Carregar cartões salvos
    void this.loadSavedCards();
  }

  private loadOrderData(): void {
    const orderId = this.route.snapshot.paramMap.get("orderId");
    if (!orderId) {
      this.snackbarService.openSnackBar(
        "ID do pedido não encontrado",
        "Fechar",
        "error",
      );
      this.router.navigate(["/my-orders"]);
      return;
    }

    // Load order details from the service
    this.cartService.getMyOrders("future").subscribe({
      next: (response) => {
        const order = response?.data.find((o) => o.id === parseInt(orderId));
        if (!order) {
          this.snackbarService.openSnackBar(
            "Pedido não encontrado",
            "Fechar",
            "error",
          );
          this.router.navigate(["/my-orders"]);
          return;
        }

        if (!this.isPaymentPending(order)) {
          this.snackbarService.openSnackBar(
            "Este pedido não está aguardando pagamento",
            "Fechar",
            "error",
          );
          this.router.navigate(["/my-orders"]);
          return;
        }

        this.currentOrder = order;
      },
      error: (error) => {
        console.error("Erro ao carregar pedido:", error);
        this.snackbarService.openSnackBar(
          "Erro ao carregar dados do pedido",
          "Fechar",
          "error",
        );
        this.router.navigate(["/my-orders"]);
      },
    });
  }

  private isPaymentPending(order: Order): boolean {
    const status = order.payment_status?.toLowerCase();
    return (
      status === "waiting" ||
      status === "ativa" ||
      status === "pendente" ||
      status === "pending" ||
      status === "recebivel" ||
      status === "unpaid"
    );
  }

  loadEventImage(event_thumb: string): string {
    if (!event_thumb) {
      return "";
    }
    return `${environment.BLOB_URL}/${event_thumb}`;
  }

  getSubtotal(): number {
    if (!this.currentOrder?.tickets) {
      return 0;
    }
    return this.currentOrder.tickets.reduce((total, ticket) => {
      return total + parseFloat(ticket.price);
    }, 0);
  }

  private async handlePaymentMethodChange(
    method: string | null | undefined,
  ): Promise<void> {
    if (!method) {
      return;
    }

    if (method !== "credit_card") {
      this.showCreditCardForm = false;
      return;
    }

    // Mostrar formulário de cartão de crédito
    this.showCreditCardForm = true;

    // Carregar opções de parcelamento (tabela de juros vem do backend)
    void this.loadInstallmentOptions();
  }

  /**
   * Carregar opções de parcelamento disponíveis para o carrinho atual.
   */
  private async loadInstallmentOptions(): Promise<void> {
    if (this.installmentOptions.length > 0) {
      return;
    }

    try {
      const response = await firstValueFrom(
        this.paymentService.getInstallmentOptions(),
      );
      this.installmentOptions = response.installments || [];

      // Garantir um default válido no formulário
      if (this.installmentOptions.length > 0) {
        this.cardForm
          .get("installments")
          ?.setValue(this.installmentOptions[0].installments);
      }
    } catch (error) {
      console.error("Erro ao carregar opções de parcelamento:", error);
      this.installmentOptions = [];
    }
  }

  ngOnDestroy() {
    clearInterval(this.checkPaymentInterval);

    // IMPORTANTE: Adicionar delay antes de limpar WebSocket para evitar
    // desconexão prematura enquanto aguarda confirmação de pagamento
    setTimeout(() => {
      this.cleanupWebSocket();
    }, 2000); // Aguardar 2 segundos antes de limpar
  }

  /**
   * Carregar cartões salvos do usuário
   */
  private async loadSavedCards(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.paymentService.getSavedCards(),
      );
      if (response.success) {
        this.savedCards = response.cards || [];
      }
    } catch (error) {
      console.error("Erro ao carregar cartões salvos:", error);
    }
  }

  /**
   * Preencher formulário de cartão com dados do usuário
   */
  private prefillCardForm(): void {
    if (!this.currentUser || !this.cardForm) {
      return;
    }

    const fullName = [this.currentUser.name, this.currentUser.last_name]
      .filter((value) => !!value)
      .join(" ")
      .trim();

    if (!this.cardForm.get("holder_name")?.value && fullName) {
      this.cardForm.patchValue({ holder_name: fullName }, { emitEvent: false });
    }
  }

  /**
   * Resetar feedback de cartão de crédito
   */
  private resetCardFeedback(): void {
    this.cardStatusMsg = "";
    this.cardStatusOk = false;
  }

  /**
   * Definir status do pagamento com cartão
   */
  private setCardStatus(message: string, ok: boolean): void {
    this.cardStatusMsg = message;
    this.cardStatusOk = ok;
  }

  /**
   * Validar campo do formulário de cartão
   */
  invalidCard(control: string): boolean {
    const field = this.cardForm.get(control);
    return !!(field && field.touched && field.invalid);
  }

  /**
   * Processar pagamento com cartão de crédito
   */
  async onCreditCardPay(): Promise<void> {
    if (!this.showCreditCardForm || !this.currentOrder) {
      return;
    }

    // Se estiver usando cartão salvo
    if (this.selectedCardId && !this.useNewCard) {
      await this.processWithSavedCard();
      return;
    }

    // Validar formulário de cartão novo
    this.cardForm.markAllAsTouched();
    if (!this.cardForm.valid) {
      this.setCardStatus(
        "Por favor, preencha todos os campos do cartão.",
        false,
      );
      return;
    }

    await this.processWithNewCard();
  }

  /**
   * Processar pagamento com cartão salvo
   */
  private async processWithSavedCard(): Promise<void> {
    this.showPaymentOverlay = true;
    this.currentPaymentStep = "processing";
    this.paymentOverlayError = "";
    this.resetCardFeedback();
    this.cardProcessing = true;

    try {
      const installments = this.cardForm.get("installments")?.value || 1;

      const response = await firstValueFrom(
        this.paymentService.createPaymentForOrder(
          this.currentOrder!.id,
          "credit_card",
          {
            cardId: this.selectedCardId || undefined,
            installments: installments,
          },
        ),
      );

      if (!response.success) {
        throw new Error(response.message || "Falha ao processar pagamento");
      }

      // Pagamento com cartão salvo é processado automaticamente
      this.currentPaymentStep = "confirming";
      await this.confirmPayment(response.order_id!);
    } catch (error: any) {
      console.error("Erro ao processar pagamento:", error);
      this.paymentOverlayError =
        error?.message || "Erro ao processar pagamento";
      this.setCardStatus(
        error?.message || "Erro ao processar pagamento",
        false,
      );
    } finally {
      this.cardProcessing = false;
    }
  }

  /**
   * Processar pagamento com cartão novo
   */
  private async processWithNewCard(): Promise<void> {
    this.showPaymentOverlay = true;
    this.currentPaymentStep = "validating";
    this.paymentOverlayError = "";
    this.resetCardFeedback();
    this.cardProcessing = true;

    try {
      // 1. Preparar dados do cartão
      this.currentPaymentStep = "processing";
      console.log("🔍 Criando pagamento no backend com dados do cartão...");

      const cardData = this.getCardDataFromForm();
      const installments = this.cardForm.get("installments")?.value || 1;

      const response = await firstValueFrom(
        this.paymentService.createPaymentForOrder(
          this.currentOrder!.id,
          "credit_card",
          {
            cardData: cardData,
            installments: installments,
          },
        ),
      );

      if (!response.success) {
        throw new Error(response.message || "Falha ao criar pagamento");
      }

      // 2. Aguardar confirmação via WebSocket
      this.currentPaymentStep = "confirming";
      console.log("🔍 Aguardando confirmação do pagamento...");

      await this.confirmPayment(response.order_id!);
    } catch (error: any) {
      console.error("Erro ao processar pagamento:", error);
      this.paymentOverlayError =
        error?.message || "Erro ao processar pagamento";
      this.setCardStatus(
        error?.message || "Erro ao processar pagamento",
        false,
      );
    } finally {
      this.cardProcessing = false;
    }
  }

  /**
   * Extrair dados do cartão do formulário
   */
  private getCardDataFromForm(): any {
    const cardNumber = this.cardForm
      .get("card_number")
      ?.value.replace(/\s/g, ""); // Remove espaços
    const month = String(this.cardForm.get("expiry_month")?.value).padStart(
      2,
      "0",
    );
    const year = String(this.cardForm.get("expiry_year")?.value).slice(-2); // Últimos 2 dígitos

    return {
      card_number: cardNumber,
      card_holder_name: this.cardForm.get("holder_name")?.value,
      card_expiration_date: `${month}${year}`, // MMYY
      card_cvv: this.cardForm.get("cvv")?.value,
    };
  }

  /**
   * Confirmar pagamento com o backend
   */
  private async confirmPayment(orderId: number): Promise<void> {
    try {
      console.log("🔍 Aguardando confirmação do pagamento...");

      this.currentPaymentStep = "finalizing";

      // Aguardar confirmação via WebSocket
      if (orderId) {
        this.waitForWebSocketConfirmation(orderId);
      } else {
        this.proceedToSuccess();
      }
    } catch (error: any) {
      console.error("Erro ao confirmar pagamento:", error);
      this.setCardStatus("Erro ao confirmar pagamento", false);
      this.proceedToSuccess(); // Prosseguir mesmo com erro
    }
  }

  // Métodos auxiliares para o template
  shouldShowProcessingIndicator(): boolean {
    return this.cardProcessing || this.loading;
  }

  getPaymentStatusText(): string {
    if (this.cardProcessing) {
      return "Processando pagamento...";
    }
    if (this.loading) {
      return "Carregando...";
    }
    return "";
  }

  shouldShowSuccessMessage(): boolean {
    return this.cardStatusOk && this.cardStatusMsg.includes("aprovado");
  }

  shouldShowErrorMessage(): boolean {
    return !this.cardStatusOk && this.cardStatusMsg.length > 0;
  }

  get paymentStatusMessage(): string {
    return this.cardStatusMsg;
  }

  retryPayment(): void {
    this.loading = false;
    this.cardProcessing = false;
    this.resetCardFeedback();
  }

  // MÉTODO REMOVIDO - era do Stripe, não é mais necessário
  async onStripePay_OBSOLETO(): Promise<void> {
    if (false) {
      return;
    }

    // Código obsoleto removido
  }

  private safeParse<T>(raw: string | null): T | null {
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  separarDDIeNumero(celular: string): { ddi: string; numeroComDdd: string } {
    if (!celular) {
      return { ddi: "", numeroComDdd: "" };
    }
    if (!celular?.startsWith("+")) {
      throw new Error('O número fornecido deve começar com "+".');
    }

    const ddi = celular.slice(0, celular.length - 11);
    const numeroComDdd = celular.slice(-11);

    return {
      ddi,
      numeroComDdd,
    };
  }

  generatePixPayment(): void {
    if (!this.currentOrder) {
      return;
    }
    this.loading = true;
    try {
      this.paymentService
        .createPaymentForOrder(this.currentOrder.id, "pix")
        .subscribe({
          next: (response) => {
            if (response?.error) {
              this.snackbarService.openSnackBar(
                response.error,
                "Fechar",
                "error",
              );
            } else {
              // Formato de produção do PagarMe
              if (response.pix_data?.qr_code) {
                this.pixQrCodeUrl = response.pix_data.qr_code;
                this.pixQrCodeImageUrl = response.pix_data.qr_code_url || "";
                this.pixCopyPasteCode = response.pix_data.qr_code; // O qr_code é o copia e cola
                this.pixExpiresAt = response.pix_data.expires_at || "";
                this.pixAmount = response.pix_data.amount || 0;
                this.statusPaymentPix = "AGUARDANDO PAGAMENTO";
              } else {
                // Retrocompatibilidade com formato antigo
                this.pixQrCodeUrl = response.qr_code || "";
                this.pixCopyPasteCode =
                  response.pix_data?.copy_paste_code || "";
              }

              // Usar WebSocket ao invés de polling
              if (this.currentOrder?.id) {
                console.log(
                  "🔍 Iniciando monitoramento PIX via WebSocket para order:",
                  this.currentOrder.id,
                );
                this.startPixPaymentMonitoring(this.currentOrder.id);
              }
            }
          },
          error: (error) => {
            console.error("Erro ao finalizar compra:", JSON.stringify(error));
            if (error?.error?.error?.error) {
              this.snackbarService.openSnackBar(
                "Erro ao finalizar compra " + error?.error?.error?.error,
                "Fechar",
                "error",
              );

              if (
                String(error?.error?.error?.error).includes("cpf") ||
                String(error?.error?.error?.error).includes("CPF")
              ) {
                this.openModalSignUp();
              }
            } else if (error?.error?.missing_fields) {
              const missingFields = error.error.missing_fields.join(", ");
              this.snackbarService.openSnackBar(
                `Dados incompletos: ${missingFields}. Por favor, atualize seu cadastro.`,
                "Fechar",
                "error",
              );
              this.router.navigate(["/finish-sign-up"]);
            } else {
              this.snackbarService.openSnackBar(
                error.error.error || "Erro ao gerar pix",
                "Fechar",
                "error",
              );
            }
          },
        });
    } catch (error) {
      console.log("Erro ao gerar pix", error);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Iniciar monitoramento de pagamento PIX via WebSocket
   */
  private startPixPaymentMonitoring(orderId: number): void {
    console.log("🔍 Iniciando monitoramento PIX via WebSocket");
    this.statusPaymentPix = "AGUARDANDO PAGAMENTO";

    // Conectar ao WebSocket se não estiver conectado
    if (!this.webSocketService.isConnected()) {
      console.log("🔌 Conectando ao WebSocket...");
      this.webSocketService.connect().subscribe((state) => {
        if (state.connected) {
          console.log("✅ WebSocket conectado, iniciando monitoramento PIX");
          this.subscribeToPixPayment(orderId);
        } else if (state.error) {
          console.error("❌ Erro ao conectar WebSocket:", state.error);
          // Fallback para polling se WebSocket falhar
          this.fallbackToPolling();
        }
      });
    } else {
      console.log("✅ WebSocket já conectado, iniciando monitoramento PIX");
      this.subscribeToPixPayment(orderId);
    }
  }

  /**
   * Subscrever a atualizações de pagamento PIX
   */
  private subscribeToPixPayment(orderId: number): void {
    // Subscrever a atualizações de pagamento
    this.paymentStatusSubscription = this.paymentNotificationService
      .startOrderMonitoring(orderId)
      .subscribe({
        next: (update: PaymentStatusUpdate) => {
          console.log("📨 Atualização de pagamento PIX recebida:", update);
          this.handlePixPaymentUpdate(update);
        },
        error: (error: any) => {
          console.error("❌ Erro no monitoramento WebSocket PIX:", error);
          // Fallback para polling se WebSocket falhar
          this.fallbackToPolling();
        },
      });
  }

  /**
   * Processar atualização de status do PIX
   */
  private handlePixPaymentUpdate(update: PaymentStatusUpdate): void {
    console.log("📊 Status do PIX:", update.status);

    if (update.status === "succeeded" || update.status === "paid") {
      this.statusPaymentPix = "PAGO";
      console.log("🎉 Pagamento PIX confirmado via WebSocket!");

      // Limpar subscrições
      this.cleanupWebSocketSubscriptions();

      // Redirecionar para página de sucesso
      setTimeout(() => {
        this.router.navigate(["/checkout-success"], {
          queryParams: { orderId: update.order_id },
        });
      }, 1000);
    } else if (update.status === "failed" || update.status === "cancelled") {
      this.statusPaymentPix = "FALHOU";
      console.error("❌ Pagamento PIX falhou:", update.data?.failure_reason);

      this.snackbarService.openSnackBar(
        `Pagamento PIX falhou: ${update.data?.failure_reason || "Erro desconhecido"}`,
        "Fechar",
        "error",
      );

      // Limpar subscrições
      this.cleanupWebSocketSubscriptions();
    } else {
      this.statusPaymentPix = "AGUARDANDO PAGAMENTO";
    }
  }

  /**
   * Fallback para polling se WebSocket não estiver disponível
   */
  private fallbackToPolling(): void {
    console.warn("⚠️ WebSocket não disponível, usando polling como fallback");

    this.checkPaymentInterval = setInterval(() => {
      this.checkPaymentStatusViaAPI();
    }, 5000);
  }

  /**
   * Verificar status do pagamento via API (fallback)
   */
  private async checkPaymentStatusViaAPI(): Promise<void> {
    if (!this.currentOrder?.id) {
      return;
    }

    try {
      const response = await firstValueFrom(
        this.paymentService.checkPaymentStatus(this.currentOrder.id),
      );

      if (response.status === "succeeded" || response.status === "paid") {
        this.statusPaymentPix = "PAGO";
        clearInterval(this.checkPaymentInterval);

        this.router.navigate(["/checkout-success"], {
          queryParams: { orderId: this.currentOrder.id },
        });
      }
    } catch (error) {
      console.error("Erro ao verificar status:", error);
    }
  }

  copyPixKey(): void {
    if (this.pixCopyPasteCode) {
      const input = this.document.createElement("input");
      input.style.position = "fixed";
      input.style.opacity = "0";
      input.value = this.pixCopyPasteCode;
      this.document.body.appendChild(input);

      input.select();
      input.setSelectionRange(0, 99999);

      this.document.execCommand("copy");

      this.document.body.removeChild(input);

      this.snackbarService.openSnackBar(
        "Pix copia e cola copiado com sucesso!",
        "Fechar",
        "success",
      );
    } else {
      this.snackbarService.openSnackBar(
        "Erro ao copiar pix!",
        "Fechar",
        "erro",
      );
    }
  }

  /**
   * Formatar data de expiração do PIX
   */
  getPixExpirationTime(): string {
    if (!this.pixExpiresAt) {
      return "";
    }

    try {
      const expiresDate = new Date(this.pixExpiresAt);
      const now = new Date();
      const diffMs = expiresDate.getTime() - now.getTime();
      const diffMinutes = Math.floor(diffMs / 60000);

      if (diffMinutes <= 0) {
        return "Expirado";
      }

      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;

      if (hours > 0) {
        return `Expira em ${hours}h ${minutes}min`;
      } else {
        return `Expira em ${minutes} minutos`;
      }
    } catch (error) {
      return "";
    }
  }

  /**
   * Formatar valor do PIX
   */
  getPixAmountFormatted(): string {
    if (!this.pixAmount) {
      return "";
    }

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(this.pixAmount);
  }

  // Métodos para formatação dos cartões salvos
  formatCardNumber(card: SavedCard): string {
    // 12 asteriscos organizados em grupos de 4, seguidos pelos últimos 4 dígitos
    const maskedDigits = "**** **** ****";
    return `${maskedDigits} ${card.last_four_digits}`;
  }

  formatExpiryDate(card: SavedCard): string {
    const month = card.exp_month.toString().padStart(2, "0");
    const year = card.exp_year.toString().slice(-2);
    return `${month}/${year}`;
  }

  getCardBrandIcon(brand: string): string {
    const brandIcons: { [key: string]: string } = {
      Visa: "💳",
      Mastercard: "💳",
      "American Express": "💳",
      Elo: "💳",
      Hipercard: "💳",
    };
    return brandIcons[brand] || "💳";
  }

  goToMyOrders(): void {
    this.router.navigate(["/my-orders"]);
  }

  changePaymentMethod(newValue: any) {
    this.checkoutForm.patchValue({
      paymentMethod: newValue,
    });
  }

  async consultaCEP() {
    const cep = this.checkoutForm.value.zip.replace("-", "");

    if (cep.length < 8) {
      this.snackbarService.openSnackBar(
        "Por favor informe um CEP válido!",
        "Fechar",
        "error",
      );
      return;
    }

    this.utilsService.consultaCEP(cep).subscribe({
      next: (response: CepResponse) => {
        this.checkoutForm.patchValue({
          street: response.logradouro,
          neighborhood: response.bairro,
          city: response.localidade,
          state: response.uf,
        });

        this.checkoutForm.get("neighborhood")?.disable();
        this.checkoutForm.get("city")?.disable();
        this.checkoutForm.get("state")?.disable();

        this.showAddressFields = true;
      },
      error: (error: any) => {
        console.error("Erro ao carregar CEP:", error);
        this.snackbarService.openSnackBar(
          "Erro ao buscar CEP: O CEP informado não foi encontrado",
          "Fechar",
          "error",
        );
      },
    });
  }

  async onSubmit(): Promise<void> {
    if (this.showCreditCardForm) {
      return await this.onCreditCardPay();
    }

    this.loading = true;

    try {
      if (this.checkoutForm.value.paymentMethod === "pix") {
        this.generatePixPayment();
        return;
      }

      if (this.checkoutForm.value.paymentMethod === "credit_card") {
        await this.onCreditCardPay();
        return;
      }

      this.snackbarService.openSnackBar(
        "Formulário inválido!",
        "Fechar",
        "error",
      );
    } catch (error) {
      console.log("Erro ao finalizar compra", error);
      this.snackbarService.openSnackBar(
        "Erro ao finalizar compra!",
        "Fechar",
        "error",
      );
    } finally {
      this.loading = false;
    }
  }

  openModalSignUp() {
    this.dialog.open(ModalSignupComponent, {
      width: "800px",
      maxWidth: "95vw",
    });
  }

  onPaymentSucceeded(orderId: number) {
    console.log("✅ Pagamento confirmado! Order ID:", orderId);

    this.setCardStatus(
      "Pagamento confirmado! Aguardando confirmação do servidor...",
      true,
    );

    if (orderId) {
      console.log(
        "🔍 Aguardando confirmação via WebSocket para order:",
        orderId,
      );
      this.waitForWebSocketConfirmation(orderId);
    } else {
      console.warn("⚠️ OrderId não encontrado, prosseguindo sem WebSocket");
      this.proceedToSuccess();
    }
  }

  private waitForWebSocketConfirmation(orderId: number): void {
    if (!this.webSocketService.isConnected()) {
      console.log("🔌 Conectando ao WebSocket...");
      this.webSocketService.connect().subscribe((state) => {
        if (state.connected) {
          console.log("✅ WebSocket conectado, iniciando monitoramento");
          this.startPaymentMonitoring(orderId);
        } else if (state.error) {
          console.error("❌ Erro ao conectar WebSocket:", state.error);
          this.handleWebSocketError();
        }
      });
    } else {
      console.log("✅ WebSocket já conectado, iniciando monitoramento");
      this.startPaymentMonitoring(orderId);
    }
  }

  private startPaymentMonitoring(orderId: number): void {
    // Configurar timeout de 45 segundos para aguardar confirmação
    // (aumentado de 30s para dar tempo ao webhook processar)
    const timeout = setTimeout(() => {
      console.warn("⏰ Timeout aguardando confirmação WebSocket (45s)");
      this.handleWebSocketTimeout();
    }, 45000);

    this.paymentStatusSubscription = this.paymentNotificationService
      .startOrderMonitoring(orderId)
      .subscribe({
        next: (update: PaymentStatusUpdate) => {
          console.log("📨 Atualização de pagamento recebida:", update);
          this.handlePaymentStatusUpdate(update, timeout);
        },
        error: (error: any) => {
          console.error("❌ Erro no monitoramento WebSocket:", error);
          clearTimeout(timeout);
          this.handleWebSocketError();
        },
      });
  }

  private handlePaymentStatusUpdate(
    update: PaymentStatusUpdate,
    timeout: NodeJS.Timeout,
  ): void {
    clearTimeout(timeout);

    if (update.type === "payment_confirmed" && update.status === "succeeded") {
      console.log("🎉 Pagamento confirmado via WebSocket!");
      this.setCardStatus(
        "Pagamento confirmado pelo servidor! Finalizando...",
        true,
      );
      this.proceedToSuccess();
    } else if (update.type === "payment_failed") {
      console.error(
        "❌ Pagamento falhou no servidor:",
        update.data?.failure_reason,
      );
      this.handlePaymentFailure(
        update.data?.failure_reason || "Erro desconhecido",
      );
    } else {
      console.log("📊 Status intermediário:", update.status);
      this.setCardStatus(`Status: ${update.status}...`, true);
    }
  }

  private proceedToSuccess(): void {
    setTimeout(() => {
      this.showPaymentOverlay = false;

      this.setCardStatus(
        "🎉 Pagamento realizado com sucesso! Redirecionando...",
        true,
      );

      setTimeout(() => {
        this.router.navigate(["/checkout-success"], {
          queryParams: { orderId: this.currentOrder?.id },
        });
      }, 2000);
    }, 1500);
  }

  private handleWebSocketTimeout(): void {
    console.warn(
      "⏰ Timeout aguardando confirmação WebSocket, prosseguindo...",
    );
    this.setCardStatus("Pagamento processado! Verificando status...", true);

    if (this.currentOrder?.id) {
      this.checkPaymentStatusFallback();
    } else {
      this.proceedToSuccess();
    }
  }

  private handleWebSocketError(): void {
    console.error("❌ Erro no WebSocket, usando fallback");
    this.setCardStatus("Verificando pagamento...", true);

    if (this.currentOrder?.id) {
      this.checkPaymentStatusFallback();
    } else {
      this.proceedToSuccess();
    }
  }

  private checkPaymentStatusFallback(): void {
    if (!this.currentOrder?.id) {
      this.proceedToSuccess();
      return;
    }

    this.paymentService.checkPaymentStatus(this.currentOrder.id).subscribe({
      next: (response) => {
        if (response.status && response.status === "succeeded") {
          console.log("✅ Pagamento confirmado via API fallback");
          this.proceedToSuccess();
        } else {
          console.warn(
            "⚠️ Status de pagamento não confirmado via API:",
            response,
          );
          this.proceedToSuccess();
        }
      },
      error: (error) => {
        console.error("❌ Erro ao verificar status via API:", error);
        this.proceedToSuccess();
      },
    });
  }

  private handlePaymentFailure(reason: string): void {
    this.showPaymentOverlay = false;
    this.setCardStatus(`Pagamento falhou: ${reason}`, false);
    this.cleanupWebSocketSubscriptions();
  }

  private cleanupWebSocketSubscriptions(): void {
    console.log(
      "🧹 [cleanupWebSocketSubscriptions] Limpando subscrições do componente",
    );

    if (this.paymentStatusSubscription) {
      console.log("   - Cancelando subscription do componente");
      this.paymentStatusSubscription.unsubscribe();
      this.paymentStatusSubscription = undefined;
    }

    // IMPORTANTE: NÃO chamar stopOrderMonitoring() imediatamente
    // O PaymentNotificationService tem seu próprio timeout de 20s
    // Apenas cancelamos a subscrição do componente ao Observable
    console.log(
      "   - Mantendo monitoramento ativo no service (auto-cleanup em 20s)",
    );
  }

  onPaymentFailed(error: any) {
    console.error("❌ Pagamento falhou:", error);

    this.showPaymentOverlay = false;
    this.cardProcessing = false;

    const errorMessage = error?.message || "Erro ao processar pagamento";
    this.setCardStatus(errorMessage, false);

    this.cleanupWebSocketSubscriptions();
  }

  // Métodos obsoletos removidos - não são mais necessários

  private getPaymentErrorMessage_OBSOLETO_REMOVED(err: any): string {
    if (false) {
      return "";
    }
    const declineCodeMessages: { [key: string]: string } = {
      insufficient_funds:
        "Saldo insuficiente no cartão. Verifique seu saldo ou use outro cartão.",
      card_declined:
        "Seu cartão foi recusado. Verifique os dados ou use outro cartão.",
      expired_card: "Cartão expirado. Verifique a data de validade.",
      incorrect_cvc: "Código de segurança (CVC) incorreto.",
      incorrect_number: "Número do cartão incorreto.",
      invalid_expiry_month: "Mês de expiração inválido.",
      invalid_expiry_year: "Ano de expiração inválido.",
      invalid_cvc: "Código de segurança (CVC) inválido.",
      processing_error:
        "Erro no processamento. Tente novamente em alguns instantes.",
      authentication_required: "Seu banco requer autenticação adicional.",
      generic_decline: "Transação não autorizada pelo banco.",
      lost_card: "Cartão reportado como perdido ou roubado.",
      stolen_card: "Cartão reportado como perdido ou roubado.",
      withdrawal_count_limit_exceeded:
        "Limite de tentativas excedido. Tente mais tarde.",
      duplicate_transaction: "Transação duplicada detectada.",
      fraudulent: "Transação bloqueada por suspeita de fraude.",
      merchant_blacklist: "Cartão não aceito por este estabelecimento.",
      new_account_information_available:
        "Informações do cartão desatualizadas.",
      no_action_taken: "Nenhuma ação foi tomada. Tente novamente.",
      not_permitted: "Transação não permitida.",
      pickup_card: "Cartão deve ser retido. Entre em contato com seu banco.",
      pin_try_exceeded: "Limite de tentativas de PIN excedido.",
      restricted_card: "Cartão com restrições. Entre em contato com seu banco.",
      revocation_of_all_authorizations: "Autorizações canceladas.",
      security_violation: "Violação de segurança detectada.",
      service_not_allowed: "Serviço não permitido.",
      stop_payment_order: "Ordem de parada de pagamento ativa.",
      testmode_decline: "Cartão de teste recusado.",
      transaction_not_allowed: "Transação não permitida.",
      try_again_later: "Tente novamente mais tarde.",
      withdrawal_limit_exceeded: "Limite de saques excedido.",
    };

    if (err.decline_code && declineCodeMessages[err.decline_code]) {
      return declineCodeMessages[err.decline_code];
    }

    if (err.code && declineCodeMessages[err.code]) {
      return declineCodeMessages[err.code];
    }

    if (err.type === "card_error") {
      return "Erro no cartão de crédito. Verifique os dados e tente novamente.";
    }

    if (err.type === "validation_error") {
      return "Dados do cartão inválidos. Verifique as informações e tente novamente.";
    }

    if (err.type === "api_error") {
      return "Erro temporário no sistema. Tente novamente em alguns instantes.";
    }

    if (err.type === "authentication_error") {
      return "Erro de autenticação. Tente novamente.";
    }

    return err.message || "Ocorreu um erro no pagamento. Tente novamente.";
  }

  // Métodos duplicados removidos - já existem nas linhas 447-478

  get isWebSocketConnected(): boolean {
    return this.webSocketService.isConnected();
  }

  private initializeWebSocket(): void {
    console.log(
      "🔌 WebSocket será inicializado automaticamente quando necessário",
    );
  }

  private unsubscribeFromPaymentUpdates(): void {
    if (this.paymentStatusSubscription) {
      this.paymentStatusSubscription.unsubscribe();
      this.paymentStatusSubscription = undefined;
    }
  }

  private cleanupWebSocket(): void {
    console.log("🧹 [cleanupWebSocket] Limpando WebSocket do componente...");

    this.cleanupWebSocketSubscriptions();
    this.unsubscribeFromPaymentUpdates();

    // IMPORTANTE: Adicionar delay antes de parar o monitoramento
    // para dar tempo de receber mensagens pendentes do WebSocket
    setTimeout(() => {
      console.log("🛑 Parando monitoramento após delay de segurança");
      this.paymentNotificationService.stopOrderMonitoring();
    }, 3000); // 3 segundos de delay
  }
}
